import React, { useState } from 'react';
import { X, Award, QrCode, Copy, Trash2, Edit3, Check, Phone, Mail, Calendar, ShoppingCart, TrendingUp, Clock, Hash, Cake, FileText, Star, Save } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import CustomerInteractions from '../../../components/CustomerInteractions';
import QuickPurchase from '../../../components/QuickPurchase';
import { calculateLoyaltyLevel, calculateVisitFrequencyRole } from '../../../utils/loyalty';
import { anonymizeName, anonymizePhone, anonymizeEmail } from '../../../utils/customer';
import { useAlert } from '../../../context/AlertContext';

const capitalizeWords = (str) => {
    if (!str) return '';
    return str.split(' ').map(word =>
        word.length > 0 ? word.charAt(0).toLocaleUpperCase('tr') + word.slice(1).toLocaleLowerCase('tr') : ''
    ).join(' ');
};

const CustomerDetailModal = ({ customer, tenantData, onClose, onDelete, onUpdate, canDelete, t }) => {
    const [activeSection, setActiveSection] = useState('overview');
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const { showAlert, showConfirm } = useAlert();

    if (!customer) return null;
    const level = calculateLoyaltyLevel(customer, tenantData);
    const visitRole = calculateVisitFrequencyRole(customer, tenantData);

    const avgBasket = customer.purchaseCount > 0 ? ((customer.totalSpent || 0) / customer.purchaseCount).toFixed(0) : 0;

    const startEdit = () => {
        setEditForm({
            firstName: customer.firstName || '',
            lastName: customer.lastName || '',
            email: customer.email || '',
            phone: customer.phone || '',
            notes: customer.notes || '',
        });
        setIsEditing(true);
    };

    const handleSaveEdit = async () => {
        if (!onUpdate) return;

        const confirmed = await showConfirm('Müşteri bilgilerini güncellemek istediğinize emin misiniz?', {
            title: 'Müşteriyi Güncelle',
            confirmText: 'Güncelle',
            type: 'info'
        });
        if (!confirmed) return;

        setSaving(true);
        try {
            await onUpdate(customer.id, editForm);
            setIsEditing(false);
            showAlert('Müşteri bilgileri güncellendi.', 'success');
        } catch (err) {
            console.error(err);
            showAlert('Güncelleme sırasında hata oluştu.', 'error');
        } finally {
            setSaving(false);
        }
    };


    const handleCopyId = () => {
        navigator.clipboard.writeText(customer.shortId || customer.id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const formatDate = (d) => {
        if (!d) return '-';
        const date = d.toDate ? d.toDate() : new Date(d);
        return date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
    };

    const formatRelativeDate = (d) => {
        if (!d) return 'Henüz yok';
        const date = new Date(d);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffDays === 0) return 'Bugün';
        if (diffDays === 1) return 'Dün';
        if (diffDays < 7) return `${diffDays} gün önce`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} hafta önce`;
        return date.toLocaleDateString('tr-TR');
    };

    const sectionBtnStyle = (section) => ({
        padding: '8px 16px',
        borderRadius: '10px',
        border: 'none',
        background: activeSection === section ? 'var(--primary)' : 'transparent',
        color: activeSection === section ? 'var(--text-on-primary)' : 'var(--text-muted)',
        fontWeight: 600,
        fontSize: '0.8125rem',
        cursor: 'pointer',
        transition: 'all 0.2s',
        whiteSpace: 'nowrap'
    });

    return (
        <div className="profile-modal-overlay" onClick={onClose}>
            <div className="card glass shadow-2xl" style={{
                maxWidth: '720px', width: '100%', padding: 0, overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
                maxHeight: '92vh', display: 'flex', flexDirection: 'column'
            }} onClick={e => e.stopPropagation()}>

                {/* Header - Customer Profile Card */}
                <div className="customer-detail-header" style={{
                    padding: '2rem',
                    background: 'linear-gradient(135deg, rgba(var(--primary-rgb), 0.06), rgba(var(--primary-rgb), 0.02))',
                    borderBottom: '1px solid var(--border)',
                    position: 'relative'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{
                                width: '56px', height: '56px',
                                background: `${level.color}18`,
                                color: level.color,
                                borderRadius: '18px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '1.1rem', fontWeight: 800,
                                border: `2px solid ${level.color}30`
                            }}>
                                {(() => {
                                    if (customer.firstName && customer.lastName) return (customer.firstName[0] + customer.lastName[0]).toUpperCase();
                                    if (customer.firstName) return customer.firstName[0].toUpperCase();
                                    return '?';
                                })()}
                            </div>
                            <div>
                                <h2 style={{ fontWeight: 800, fontSize: '1.25rem', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {isEditing ? (
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input value={editForm.firstName} onChange={e => setEditForm({ ...editForm, firstName: capitalizeWords(e.target.value) })}
                                                style={{ width: '110px', height: '32px', fontSize: '0.9rem', fontWeight: 700, borderRadius: '8px', border: '1px solid var(--border)', padding: '0 8px' }} placeholder="Ad" />
                                            <input value={editForm.lastName} onChange={e => setEditForm({ ...editForm, lastName: capitalizeWords(e.target.value) })}
                                                style={{ width: '110px', height: '32px', fontSize: '0.9rem', fontWeight: 700, borderRadius: '8px', border: '1px solid var(--border)', padding: '0 8px' }} placeholder="Soyad" />
                                        </div>
                                    ) : (
                                        anonymizeName(`${customer.firstName || ''} ${customer.lastName || ''}`) || 'İsimsiz Müşteri'
                                    )}
                                </h2>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px', flexWrap: 'wrap' }}>
                                    <span style={{
                                        fontSize: '0.7rem', fontWeight: 700,
                                        background: `${level.color}18`, color: level.color,
                                        padding: '3px 10px', borderRadius: '6px'
                                    }}>
                                        {level.label}
                                    </span>
                                    {visitRole && (
                                        <span style={{
                                            fontSize: '0.7rem', fontWeight: 700,
                                            background: `${visitRole.color}12`, color: visitRole.color,
                                            padding: '3px 10px', borderRadius: '6px'
                                        }}>
                                            {visitRole.label}
                                        </span>
                                    )}
                                    {customer.shortId && (
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                                            #{customer.shortId}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            {isEditing ? (
                                <>
                                    <button onClick={() => setIsEditing(false)} style={{
                                        background: 'var(--bg-main)', border: '1px solid var(--border)',
                                        borderRadius: '10px', padding: '8px 12px', cursor: 'pointer',
                                        color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600
                                    }}>İptal</button>
                                    <button onClick={handleSaveEdit} disabled={saving} style={{
                                        background: 'var(--primary)', border: 'none',
                                        borderRadius: '10px', padding: '8px 14px', cursor: 'pointer',
                                        color: 'white', fontSize: '0.75rem', fontWeight: 700,
                                        display: 'flex', alignItems: 'center', gap: '4px',
                                        opacity: saving ? 0.6 : 1
                                    }}>
                                        <Save size={14} /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                                    </button>
                                </>
                            ) : (
                                <button onClick={startEdit} style={{
                                    background: 'var(--bg-main)', border: '1px solid var(--border)',
                                    borderRadius: '10px', width: '34px', height: '34px', cursor: 'pointer',
                                    color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <Edit3 size={15} />
                                </button>
                            )}
                             <button onClick={onClose} style={{
                                 background: 'rgba(0,0,0,0.05)',
                                 border: 'none',
                                 cursor: 'pointer',
                                 padding: '8px',
                                 borderRadius: '12px',
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 color: 'var(--text-main)',
                                 transition: 'all 0.2s',
                                 outline: 'none'
                             }}
                             onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                             onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                             >
                                 <X size={20} strokeWidth={2.5} />
                             </button>
                        </div>
                    </div>

                    {/* Contact Info Row */}
                    <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.8125rem' }}>
                        {isEditing ? (
                            <div style={{ display: 'flex', gap: '8px', flex: 1, flexWrap: 'wrap' }}>
                                <div style={{ position: 'relative', flex: 1, minWidth: '180px' }}>
                                    <Mail size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                    <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value.toLowerCase() })}
                                        placeholder="E-posta" style={{ width: '100%', height: '36px', paddingLeft: '32px', fontSize: '0.8125rem', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                </div>
                                <div style={{ position: 'relative', flex: 1, minWidth: '150px' }}>
                                    <Phone size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                    <input type="tel" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value.replace(/[^0-9]/g, '') })}
                                        placeholder="Telefon" style={{ width: '100%', height: '36px', paddingLeft: '32px', fontSize: '0.8125rem', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                </div>
                            </div>
                        ) : (
                            <>
                                {customer.email && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)' }}>
                                        <Mail size={14} /> {anonymizeEmail(customer.email)}
                                    </div>
                                )}
                                {customer.phone && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)' }}>
                                        <Phone size={14} /> {anonymizePhone(customer.phone)}
                                    </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)' }}>
                                    <Calendar size={14} /> Kayıt: {formatDate(customer.createdAt)}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Stats Row */}
                <div className="customer-detail-stats" style={{
                    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                    borderBottom: '1px solid var(--border)'
                }}>
                    {[
                        { label: 'Puan', value: customer.points || 0, icon: <Star size={16} />, color: '#f59e0b' },
                        { label: 'Toplam Harcama', value: `₺${(customer.totalSpent || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`, icon: <TrendingUp size={16} />, color: '#3b82f6' },
                        { label: 'Sipariş', value: customer.purchaseCount || 0, icon: <ShoppingCart size={16} />, color: '#10b981' },
                        { label: 'Ort. Sepet', value: `₺${avgBasket}`, icon: <Hash size={16} />, color: '#8b5cf6' },
                    ].map((stat, i) => (
                        <div key={i} style={{
                            padding: '1rem 1.25rem', textAlign: 'center',
                            borderRight: i < 3 ? '1px solid var(--border)' : 'none',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '4px' }}>
                                <span style={{ color: stat.color }}>{stat.icon}</span>
                                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>{stat.label}</span>
                            </div>
                            <div style={{ fontWeight: 800, fontSize: '1.15rem', color: 'var(--text-main)' }}>{stat.value}</div>
                        </div>
                    ))}
                </div>

                {/* Section Tabs */}
                <div className="customer-detail-tabs" style={{
                    display: 'flex', gap: '4px', padding: '0.75rem 2rem',
                    borderBottom: '1px solid var(--border)',
                    background: 'rgba(var(--primary-rgb), 0.02)',
                    overflowX: 'auto'
                }}>
                    <button style={sectionBtnStyle('overview')} onClick={() => setActiveSection('overview')}>Puan Yükle</button>
                    <button style={sectionBtnStyle('history')} onClick={() => setActiveSection('history')}>İşlem Geçmişi</button>
                    <button style={sectionBtnStyle('qr')} onClick={() => setActiveSection('qr')}>Sadakat Kartı</button>
                </div>

                {/* Scrollable Content */}
                <div className="customer-detail-body" style={{ flex: 1, overflowY: 'auto', padding: '1.5rem 2rem' }}>

                    {activeSection === 'overview' && (
                        <div className="fade-in">
                            {/* Last Visit Info */}
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                padding: '0.875rem 1.25rem', marginBottom: '1.25rem',
                                background: 'var(--bg-main)', borderRadius: '12px',
                                border: '1px solid var(--border)', fontSize: '0.8125rem'
                            }}>
                                <Clock size={16} style={{ color: 'var(--text-muted)' }} />
                                <span style={{ color: 'var(--text-muted)' }}>Son ziyaret:</span>
                                <span style={{ fontWeight: 700, color: 'var(--text-main)' }}>{formatRelativeDate(customer.lastInteractionAt)}</span>
                                {customer.totalPoints > 0 && (
                                    <>
                                        <span style={{ color: 'var(--text-muted)', marginLeft: 'auto' }}>Ömür boyu puan:</span>
                                        <span style={{ fontWeight: 700, color: '#f59e0b' }}>{customer.totalPoints}</span>
                                    </>
                                )}
                            </div>

                            <QuickPurchase customerId={customer.id} />

                            {/* Notes Section (if editing) */}
                            {isEditing && (
                                <div style={{ marginTop: '1rem' }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px', display: 'block' }}>Not</label>
                                    <textarea
                                        value={editForm.notes}
                                        onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                                        placeholder="Müşteri hakkında not..."
                                        rows={3}
                                        style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', resize: 'vertical', fontFamily: 'inherit' }}
                                    />
                                </div>
                            )}

                            {/* Display notes if not editing and notes exist */}
                            {!isEditing && customer.notes && (
                                <div style={{
                                    marginTop: '1rem', padding: '1rem 1.25rem',
                                    background: 'rgba(245, 158, 11, 0.05)',
                                    border: '1px solid rgba(245, 158, 11, 0.15)',
                                    borderRadius: '12px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                        <FileText size={14} style={{ color: '#f59e0b' }} />
                                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase' }}>Not</span>
                                    </div>
                                    <p style={{ fontSize: '0.875rem', color: 'var(--text-main)', lineHeight: 1.5, margin: 0 }}>{customer.notes}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeSection === 'history' && (
                        <div className="fade-in">
                            <CustomerInteractions customerId={customer.id} />
                        </div>
                    )}

                    {activeSection === 'qr' && (
                        <div className="fade-in" style={{ textAlign: 'center' }}>
                            <div style={{
                                padding: '2rem', marginBottom: '1.5rem',
                                background: 'var(--bg-main)', borderRadius: '20px',
                                border: '2px dashed var(--border)', display: 'inline-block'
                            }}>
                                <div style={{ marginBottom: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                    <QrCode size={18} style={{ color: 'var(--primary)' }} /> Sadakat Kartı QR
                                </div>
                                <div style={{ background: 'white', padding: '1.5rem', borderRadius: '16px', display: 'inline-block', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
                                    <QRCodeSVG value={customer.shortId || customer.id} size={180} />
                                </div>
                                <div style={{ marginTop: '1.25rem' }}>
                                    <button onClick={handleCopyId} className="btn-secondary" style={{
                                        fontSize: '0.8125rem', display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        padding: '8px 16px', borderRadius: '10px'
                                    }}>
                                        {copied ? <><Check size={14} /> Kopyalandı!</> : <><Copy size={14} /> ID Kopyala</>}
                                    </button>
                                </div>
                            </div>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', maxWidth: '300px', margin: '0 auto', lineHeight: 1.5 }}>
                                Bu QR kodu müşterinin check-in yapması ve puan kazanması için kullanılabilir.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer - Delete Button */}
                {canDelete && !isEditing && (
                    <div style={{ padding: '1rem 2rem', borderTop: '1px solid var(--border)' }}>
                        <button onClick={() => onDelete(customer)} style={{
                            width: '100%', background: '#fef2f2', color: '#ef4444',
                            border: '1px solid #fee2e2', padding: '10px', fontWeight: 700,
                            borderRadius: '12px', cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', gap: '8px',
                            fontSize: '0.8125rem', transition: 'all 0.2s'
                        }}>
                            <Trash2 size={16} /> Müşteriyi Sil
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                @media (max-width: 640px) {
                    .profile-modal-overlay > div {
                        max-height: 100vh !important;
                        border-radius: 0 !important;
                    }
                    .customer-detail-header { padding: 1rem !important; }
                    .customer-detail-stats { grid-template-columns: repeat(2, 1fr) !important; }
                    .customer-detail-tabs { padding: 0.75rem 1rem !important; }
                    .customer-detail-body { padding: 1rem !important; }
                }
            `}</style>
        </div>
    );
};

export default CustomerDetailModal;
